import {PHASES, WAR_PHASE_STAGES} from '../../constants/phases';
import {getPhaseIcon} from '../phase/getPhaseIcon';

export const COMPLETE_BUTTON_MODES = Object.freeze({
  // Свой ход, кто-то еще может действовать (или этап без счетчика: найм, подготовка)
  PENDING: 'pending',
  // Свой ход, делать больше нечего — даже если N < S
  READY: 'ready',
  // Ждем другого игрока; у зрителя и в записи — тоже
  WAITING: 'waiting',
  // Ближний бой, игрок еще не подтвердил этап (в своей и чужой фазе войны).
  // Пока у его отрядов боя есть непотраченные перебросы — как «есть дела»
  // (выгравированная галочка, без свечения), потом — как «готово»
  CONFIRM: 'confirm',
  // Время хода вышло, сервер еще не закрыл ход
  LOCKED: 'locked',
  // Партия закончилась
  ENDED: 'ended',
});

// Вид галочки не зависит от цвета фазы: выгравированная в эмали или белая выпуклая
export const COMPLETE_BUTTON_CHECKS = Object.freeze({
  ENGRAVED: 'engraved',
  RAISED: 'raised',
});

// Иконка дела рядом со счетчиком N/S
const DEED_ICONS = {
  [PHASES.SUPPLY]: '/img/icons/supply white.png',
  [PHASES.CONSTRUCTION]: '/img/icons/action.png',
  // Шлем d6, обрезанный по силуэту: в исходнике (unit d6 white.png, холст гекса)
  // он занимает треть картинки и в 16 px был бы втрое мельче соседних иконок
  [PHASES.WAR]: '/img/icons/unit d6 white icon.png',
};

// Подпись в углублении вместо счетчика на этапах без дел (прототип «А · Плита»)
const WELL_LABELS = {
  [WAR_PHASE_STAGES.RECRUITMENT]: 'найм',
  [WAR_PHASE_STAGES.COMBAT]: 'бой',
};

// С двузначным S счетчик набирается мельче, кнопка своего размера не меняет
const COMPACT_COUNTER_MIN_TOTAL = 10;

const getMode = ({
  phase, gameResults, isPassiveWatchMode, isTurnInputLocked, isCombat, isCombatConfirmPending, isOwnTurn, progress,
  isStartingQuarterPlaced,
}) => {
  if (gameResults) return COMPLETE_BUTTON_MODES.ENDED;
  if (isPassiveWatchMode) return COMPLETE_BUTTON_MODES.WAITING;
  if (isTurnInputLocked) return COMPLETE_BUTTON_MODES.LOCKED;
  if (isCombat) return isCombatConfirmPending ? COMPLETE_BUTTON_MODES.CONFIRM : COMPLETE_BUTTON_MODES.WAITING;
  if (!isOwnTurn) return COMPLETE_BUTTON_MODES.WAITING;
  // Расстановка: дел со счетчиком нет (phase-progress), но единственное дело
  // известно — стартовый квартал. Поставлен — делать больше нечего
  if (phase === PHASES.SETUP && isStartingQuarterPlaced) return COMPLETE_BUTTON_MODES.READY;
  // «Готово» — по пустому списку способных, а не по равенству N и S
  if (progress && progress.done !== null && progress.actionableIds.length === 0) {
    return COMPLETE_BUTTON_MODES.READY;
  }
  return COMPLETE_BUTTON_MODES.PENDING;
};

const CHECK_BY_MODE = {
  [COMPLETE_BUTTON_MODES.PENDING]: COMPLETE_BUTTON_CHECKS.ENGRAVED,
  [COMPLETE_BUTTON_MODES.READY]: COMPLETE_BUTTON_CHECKS.RAISED,
  [COMPLETE_BUTTON_MODES.CONFIRM]: COMPLETE_BUTTON_CHECKS.RAISED,
  [COMPLETE_BUTTON_MODES.ENDED]: COMPLETE_BUTTON_CHECKS.RAISED,
};

/**
 * Что показывает кнопка хода (delta-спека game-ui-design-system: счетчик,
 * галочка, «готово», ожидание, иконка этапа, портрет, свечение, режимы просмотра).
 * Чистая функция: выбор игрока для портрета и цвет кнопки считаются отдельно.
 *
 * @param {Object} params
 * @param {string} params.phase - PHASES
 * @param {string|null} params.warPhaseStage - WAR_PHASE_STAGES
 * @param {Object|null} params.gameResults - итоги партии
 * @param {Object|null} params.progress - selectPhaseProgress своего игрока
 * @param {boolean} params.isOwnTurn - ход этого клиента: кнопку можно нажать
 * @param {boolean} params.isCombatConfirmPending - ближний бой ждет подтверждения этого игрока
 * @param {boolean} params.isCombatRerollPending - у его отрядов боя еще есть переброс за
 *   дополнительное снабжение (getMeleeRerollUnitIds)
 * @param {boolean} params.isTurnInputLocked - время хода вышло (turnInputLock)
 * @param {boolean} params.isPassiveWatchMode - зритель, запись матча, демо-партия меню
 * @param {boolean} params.isStartingQuarterPlaced - в расстановке: свой стартовый квартал уже на поле
 * @returns {{
 *   mode: string,
 *   done: number|null, total: number|null, deedIcon: string|null,
 *   isCounterShown: boolean, isCounterMuted: boolean, isCounterCompact: boolean,
 *   wellLabel: string|null, stageIcon: string|null, isStageBadgeShown: boolean,
 *   check: string|null, isGlowing: boolean, isPortraitShown: boolean
 * }}
 */
export const getCompleteButtonView = ({
  phase,
  warPhaseStage,
  gameResults = null,
  progress = null,
  isOwnTurn = false,
  isCombatConfirmPending = false,
  isCombatRerollPending = false,
  isTurnInputLocked = false,
  isPassiveWatchMode = false,
  isStartingQuarterPlaced = false,
}) => {
  const isCombat = phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.COMBAT;
  const mode = getMode({
    phase, gameResults, isPassiveWatchMode, isTurnInputLocked, isCombat, isCombatConfirmPending, isOwnTurn, progress,
    isStartingQuarterPlaced,
  });

  // Счетчик — только в свой ход и только там, где дела считаются. При S = 0
  // этап для игрока пропускается, 0/0 не показываем
  const isCounterShown = (mode === COMPLETE_BUTTON_MODES.PENDING || mode === COMPLETE_BUTTON_MODES.READY)
    && progress !== null && progress.done !== null && progress.total > 0;
  const done = isCounterShown ? progress.done : null;
  const isActive = mode === COMPLETE_BUTTON_MODES.PENDING
    || mode === COMPLETE_BUTTON_MODES.READY
    || mode === COMPLETE_BUTTON_MODES.CONFIRM;
  const total = isCounterShown ? progress.total : null;
  const isCombatDeedsLeft = mode === COMPLETE_BUTTON_MODES.CONFIRM && isCombatRerollPending;

  return {
    mode,
    done,
    total,
    deedIcon: isCounterShown ? DEED_ICONS[phase] || null : null,
    isCounterShown,
    isCounterMuted: isCounterShown && mode === COMPLETE_BUTTON_MODES.READY,
    isCounterCompact: isCounterShown && total >= COMPACT_COUNTER_MIN_TOTAL,
    wellLabel: isActive && !isCounterShown && phase === PHASES.WAR ? WELL_LABELS[warPhaseStage] || null : null,
    // Иконка фазы или этапа войны: в свой и чужой ход (и у зрителя) — на
    // жетоне в углу, при блокировке по времени — на самой плите; после конца
    // партии ее нет
    stageIcon: mode === COMPLETE_BUTTON_MODES.ENDED ? null : getPhaseIcon(phase, warPhaseStage),
    isStageBadgeShown: isActive || mode === COMPLETE_BUTTON_MODES.WAITING,
    check: isCombatDeedsLeft ? COMPLETE_BUTTON_CHECKS.ENGRAVED : CHECK_BY_MODE[mode] || null,
    isGlowing: mode === COMPLETE_BUTTON_MODES.READY
      || (mode === COMPLETE_BUTTON_MODES.CONFIRM && !isCombatDeedsLeft),
    // Портрет — только в «ждите»: кого ждем
    isPortraitShown: mode === COMPLETE_BUTTON_MODES.WAITING,
  };
};
